/**
 * Angebot als PDF (0072): Schreiben der Agentur an die Marke mit Nummer, Datum und Gültigkeit, Positionen je Creator
 * (Casting), Zuschlägen mit Betrag, Zwischensumme, Provision (falls obendrauf), Gesamtsumme und dem Hinweis zu Steuer und
 * Provision. Dieselben Zahlen wie im Portal (offerLineAmounts, offerTotals). Nutzt den PDF-Schreiber der Rechnung.
 */
import { PDF_LAYOUT, MUTED, Page, assemble, belegFarben, belegLogo, fmtDate, pagesToSvg, wrapText, type InvoicePdfParty, type PdfImage } from "./invoice-pdf.js";
import { BELEG_SCHRIFT_FAKTOR, belegLayout } from "./beleg-layout.js";
import { countryName, normalizeCountry } from "./countries.js";
import type { SnapshotParty } from "./invoice-document.js";
import { formatMoney } from "./money.js";
import { taxIdLabel } from "./tax.js";
import { offerLineAmounts, offerTotals, surchargeText, type Surcharge } from "./offers.js";
import type { Cents, Currency, IsoDate, Language, MoneyLocale } from "./types.js";

export interface OfferPdfLine {
  /** „Reel · Instagram · Unboxing“ */
  description: string;
  /** Zusatz unter der Leistung (z. B. Notiz), optional */
  detail?: string | null;
  quantity: number;
  unitPriceCents: Cents;
  surcharges: Surcharge[];
}
export interface OfferPdfGroup {
  /** Creator (bei mehreren Creatorn je Gruppe); null bei nur einer Gruppe ohne Überschrift */
  heading: string | null;
  lines: OfferPdfLine[];
}
export interface OfferPdfModel {
  language: Language;
  locale: MoneyLocale;
  currency: Currency;
  /** null = Entwurf ohne Nummer */
  number: string | null;
  date: IsoDate;
  validUntil: IsoDate | null;
  sender: InvoicePdfParty;
  footerLines: string[];
  recipient: InvoicePdfParty;
  /** Betreff: Titel der Anfrage */
  title: string;
  /** Einleitung (Absätze) */
  intro: string[];
  groups: OfferPdfGroup[];
  commissionIncluded: boolean;
  agencyFeeBp: number | null;
  /** Hinweis zu Steuer und Provision (priceBasisText) */
  basisText: string;
  /** Bedingungen und Hinweise (Absätze) */
  terms: string[];
  accent?: string | null;
  logo?: PdfImage | null;
  /** PDF-Vorlage der Agentur für Angebote (beleg-layout.ts, 0456); ohne Angabe wie bisher */
  layout?: unknown;
}

const T = {
  de: {
    offer: "Angebot", draft: "Angebot · Entwurf", date: "Datum", number: "Angebotsnummer", valid: "Gültig bis",
    service: "Leistung", qty: "Menge", unit: "Einzelpreis", amount: "Betrag", sub: (n: string) => `Summe ${n}`, subtotal: "Zwischensumme",
    fee: (p: string) => `Agenturprovision ${p}`, total: "Gesamtsumme", validity: (d: string) => `Dieses Angebot gilt bis zum ${d}.`,
    page: (a: number, b: number) => `Seite ${a} von ${b}`, lineTotal: "Position gesamt",
  },
  en: {
    offer: "Offer", draft: "Offer · Draft", date: "Date", number: "Offer number", valid: "Valid until",
    service: "Service", qty: "Qty", unit: "Unit price", amount: "Amount", sub: (n: string) => `Total ${n}`, subtotal: "Subtotal",
    fee: (p: string) => `Agency commission ${p}`, total: "Total", validity: (d: string) => `This offer is valid until ${d}.`,
    page: (a: number, b: number) => `Page ${a} of ${b}`, lineTotal: "Item total",
  },
} as const;

const clean = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * Parteien des Angebots aus den eingefrorenen Angaben (app.org_party bzw. Marke mit Ansprechperson): Anschrift, Land nur,
 * wenn es vom Land der anderen Partei abweicht; die Agentur mit USt-IdNr. (nicht als Kleinunternehmer), die Marke „z. Hd.“.
 */
export function offerParties(issuer: SnapshotParty, recipient: SnapshotParty & { contact?: string | null }, lang: Language): { sender: InvoicePdfParty; recipient: InvoicePdfParty } {
  const ic = normalizeCountry(clean(issuer.country));
  const rc = normalizeCountry(clean(recipient.country));
  const lines = (p: SnapshotParty, other: string | null) => {
    const code = normalizeCountry(clean(p.country));
    const out = [clean(p.street), [clean(p.postal_code), clean(p.city)].filter(Boolean).join(" ") || null];
    if (code && code !== other) out.push(countryName(code, lang));
    return out.filter((x): x is string => !!x);
  };
  const vat = clean(issuer.vat_id) && !issuer.small_business ? [{ label: taxIdLabel(ic, lang), value: clean(issuer.vat_id)! }] : [];
  const contact = clean(recipient.contact);
  return {
    sender: { name: clean(issuer.name) ?? "–", addressLines: lines(issuer, rc), fields: vat },
    recipient: { name: clean(recipient.name) ?? "–", addressLines: [...(contact ? [`${lang === "en" ? "Attn." : "z. Hd."} ${contact}`] : []), ...lines(recipient, ic)] },
  };
}

/** Summen des Modells (dieselbe Rechnung wie im Portal) */
export function offerPdfTotals(m: Pick<OfferPdfModel, "groups" | "commissionIncluded" | "agencyFeeBp">) {
  const lines = m.groups.flatMap((g, i) => g.lines.map((l) => ({ quantity: l.quantity, unit_price_cents: l.unitPriceCents, surcharges: l.surcharges, candidate_id: String(i) })));
  return offerTotals(lines, { commissionIncluded: m.commissionIncluded, agencyFeeBp: m.agencyFeeBp });
}

/** Dateiname: Angebot_AN-2026-001_Marke.pdf (Entwurf: Angebot_Entwurf_…) */
export function offerFileName(number: string | null, brand: string, lang: Language = "de"): string {
  const word = lang === "en" ? "Offer" : "Angebot";
  const safe = (s: string) => s.normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);
  return `${word}_${number ? safe(number) : lang === "en" ? "Draft" : "Entwurf"}_${safe(brand) || "Marke"}.pdf`;
}

export function renderOfferPdf(m: OfferPdfModel, opts: { now?: Date } = {}): Uint8Array {
  const l = layoutOffer(m);
  return assemble(l.pages, l.title, opts.now ?? new Date(), l.images);
}

/** Vorschau des Angebots je Seite als SVG aus demselben Layout (PDF-Vorlagen, 0456) */
export function renderOfferSvg(m: OfferPdfModel): string[] {
  const l = layoutOffer(m);
  return pagesToSvg(l.pages, l.images);
}

function layoutOffer(m: OfferPdfModel): { pages: Page[]; title: string; images: PdfImage[] } {
  const { H, L, R, FOOTER_TOP } = PDF_LAYOUT;
  const lang: Language = m.language === "en" ? "en" : "de";
  const lay = belegLayout(m.layout);
  const bz = lay.beschriftung;
  const t = { ...T[lang], service: bz.leistung ?? T[lang].service, qty: bz.menge ?? T[lang].qty, unit: bz.einzelpreis ?? T[lang].unit, amount: bz.betrag ?? T[lang].amount };
  const farben = belegFarben(lay, m.accent);
  const accent = farben.titel;
  const linie = farben.linie;
  const k = BELEG_SCHRIFT_FAKTOR[lay.schrift];
  const fs = (v: number) => Math.round(v * k * 100) / 100;
  const money = (c: number) => formatMoney(c, m.currency, m.locale);
  const d = (v: string | null | undefined) => fmtDate(v ?? null, lang, m.locale);
  const pages: Page[] = [];
  let page = new Page();
  pages.push(page);
  let y = H - 56;
  const footer = [...m.footerLines, ...(lay.fusstext ? [lay.fusstext] : [])].map((x) => x.trim()).filter(Boolean).slice(0, 5);
  const bottom = FOOTER_TOP + 12 + footer.length * 10;
  const colQty = R - 190, colUnit = R - 95;
  const tableHead = () => {
    if (farben.fuellung) page.rect(L - 6, y - 7, R - L + 12, fs(9) + 12, farben.fuellung);
    page.text(L, y, t.service, "F2", fs(9), MUTED);
    page.right(colQty, y, t.qty, "F2", fs(9), MUTED);
    page.right(colUnit, y, t.unit, "F2", fs(9), MUTED);
    page.right(R, y, t.amount, "F2", fs(9), MUTED);
    y -= 6;
    if (!farben.fuellung) page.line(L, y, R, y, 0.8, linie);
    y -= 14;
  };
  let inTable = false;
  const ensure = (need: number) => {
    if (y - need < bottom) {
      page = new Page(); pages.push(page); y = H - 56;
      if (inTable) tableHead();
    }
  };

  const gezeichnet = belegLogo(page, lay, m.logo, y);
  const logo = gezeichnet.logo;
  y = gezeichnet.y;
  for (const part of wrapText(m.sender.name || "–", "F2", fs(15), R - L)) { page.text(L, y, part, "F2", fs(15)); y -= fs(18); }
  const senderLine = [...(lay.kopfAdresse ? m.sender.addressLines : []), ...(m.sender.fields ?? []).map((f) => `${f.label}: ${f.value}`)].filter(Boolean).join(" · ");
  for (const part of wrapText(senderLine, "F1", fs(9), R - L)) { page.text(L, y, part, "F1", fs(9), MUTED); y -= fs(12); }
  y -= 24;

  // Empfänger links, Angaben rechts
  const top = y;
  page.text(L, y, wrapText([m.sender.name, ...m.sender.addressLines].join(" · "), "F1", 7, 250)[0] ?? "", "F1", 7, MUTED);
  y -= 18;
  for (const part of wrapText(m.recipient.name || "–", "F2", 11, 250)) { page.text(L, y, part, "F2", 11); y -= 14; }
  for (const a of m.recipient.addressLines) for (const part of wrapText(a, "F1", 11, 250)) { page.text(L, y, part, "F1", 11); y -= 14; }
  const meta: [string, string][] = [[t.date, d(m.date) ?? ""]];
  if (m.number) meta.push([t.number, m.number]);
  if (m.validUntil) meta.push([t.valid, d(m.validUntil) ?? ""]);
  meta.forEach(([k, v], i) => {
    page.text(340, top - i * 15, k, "F1", 9, MUTED);
    page.right(R, top - i * 15, v, "F1", 10);
  });
  y = Math.min(y, top - meta.length * 15) - 24;

  page.text(L, y, m.number ? `${t.offer} ${m.number}` : t.draft, "F2", fs(13), accent);
  y -= fs(17);
  for (const part of wrapText(m.title, "F2", fs(11), R - L)) { page.text(L, y, part, "F2", fs(11)); y -= fs(14); }
  y -= 8;
  for (const para of [...m.intro, ...(lay.hinweis ? lay.hinweis.split("\n") : [])].filter((p) => p.trim())) {
    const lines = wrapText(para, "F1", fs(10), R - L);
    ensure(lines.length * fs(13) + 8);
    for (const line of lines) { page.text(L, y, line, "F1", fs(10)); y -= fs(13); }
    y -= 8;
  }

  // Positionen
  y -= 4;
  ensure(60);
  inTable = true;
  tableHead();
  const totals = offerPdfTotals(m);
  const many = m.groups.length > 1;
  m.groups.forEach((g, gi) => {
    if (g.heading) {
      ensure(30);
      page.text(L, y, g.heading, "F2", fs(10.5), accent);
      y -= 15;
    }
    for (const l of g.lines) {
      const a = offerLineAmounts({ quantity: l.quantity, unit_price_cents: l.unitPriceCents, surcharges: l.surcharges });
      const desc = wrapText(l.description, "F1", 10, colQty - L - 50);
      const detail = l.detail?.trim() ? wrapText(l.detail.trim(), "F1", 8.5, colQty - L - 50) : [];
      ensure(desc.length * 13 + detail.length * 11 + a.surcharges.length * 12 + 20);
      desc.forEach((s, i) => page.text(L, y - i * 13, s, "F1", 10));
      page.right(colQty, y, String(l.quantity), "F1", 10);
      page.right(colUnit, y, money(l.unitPriceCents), "F1", 10);
      page.right(R, y, money(a.baseCents), "F1", 10);
      y -= desc.length * 13;
      for (const s of detail) { page.text(L + 8, y, s, "F1", 8.5, MUTED); y -= 11; }
      for (const s of a.surcharges) {
        const txt = wrapText(`+ ${surchargeText(s, lang)}`, "F1", 9, colUnit - L - 20)[0] ?? "";
        page.text(L + 8, y, txt, "F1", 9, MUTED);
        page.right(R, y, money(s.cents), "F1", 9, MUTED);
        y -= 12;
      }
      if (a.surcharges.length) {
        page.text(L + 8, y, t.lineTotal, "F1", 9);
        page.right(R, y, money(a.totalCents), "F2", 9.5);
        y -= 12;
      }
      y -= 4;
      page.line(L, y + 2, R, y + 2, 0.3);
      y -= 8;
    }
    if (many) {
      ensure(18);
      page.text(L, y, t.sub(g.heading ?? ""), "F2", 9.5);
      page.right(R, y, money(totals.byCandidate[String(gi)] ?? 0), "F2", 9.5);
      y -= 18;
    }
  });
  inTable = false;

  // Summen
  ensure(60);
  const sumRow = (label: string, cents: number, bold = false) => {
    page.text(R - 250, y, label, bold ? "F2" : "F1", bold ? 11 : 10);
    page.right(R, y, money(cents), bold ? "F2" : "F1", bold ? 11 : 10);
    y -= bold ? 16 : 14;
  };
  if (totals.feeCents > 0) {
    sumRow(t.subtotal, totals.subtotalCents);
    const p = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }).format((m.agencyFeeBp ?? 0) / 100);
    sumRow(t.fee(lang === "en" ? `${p}%` : `${p} %`), totals.feeCents);
  }
  if (farben.fuellung) page.rect(R - 256, y - 6, 262, 20, farben.fuellung);
  else page.line(R - 250, y + 10, R, y + 10, 0.8, linie);
  y -= 4;
  sumRow(t.total, totals.totalCents, true);
  y -= 10;

  const paras = [m.basisText, ...(m.validUntil ? [t.validity(d(m.validUntil) ?? "")] : []), ...m.terms, ...(lay.bedingungen ? lay.bedingungen.split("\n") : [])].filter((p) => p && p.trim());
  for (const para of paras) {
    const lines = wrapText(para, "F1", 9.5, R - L);
    ensure(lines.length * 12.5 + 6);
    for (const line of lines) { page.text(L, y, line, "F1", 9.5); y -= 12.5; }
    y -= 6;
  }

  pages.forEach((p, n) => {
    p.line(L, FOOTER_TOP, R, FOOTER_TOP, 0.4);
    let fy = FOOTER_TOP - 12;
    for (const f of footer) { p.text(L, fy, wrapText(f, "F1", 7.5, R - L - 70)[0] ?? "", "F1", 7.5, MUTED); fy -= 10; }
    if (lay.seitenzahlen) p.right(R, FOOTER_TOP - 12, t.page(n + 1, pages.length), "F1", 7.5, MUTED);
    if (lay.design === "akzent") p.rect(0, H - 10, PDF_LAYOUT.W, 10, farben.marke);
  });
  return { pages, title: m.number ? `${t.offer} ${m.number}` : t.draft, images: logo ? [logo] : [] };
}
